// Document utility functions

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

// Maximum file size: 10MB (matching backend validation)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed file extensions (matching backend validation)
const ALLOWED_EXTENSIONS = new Set([
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', 
  '.txt', '.rtf', '.jpg', '.jpeg', '.png', 
  '.gif', '.bmp', '.tiff', '.csv'
]);

/**
 * Validates a file for upload
 */
export const validateFile = (file: File): FileValidationResult => {
  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  if (file.size === 0) {
    return { isValid: false, error: 'File is empty' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { 
      isValid: false, 
      error: `File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(MAX_FILE_SIZE)})` 
    };
  }

  const extension = getFileExtension(file.name).toLowerCase();
  if (!ALLOWED_EXTENSIONS.has(extension)) {
    return { 
      isValid: false, 
      error: `File type '${extension}' is not allowed. Allowed types: ${Array.from(ALLOWED_EXTENSIONS).join(', ')}` 
    };
  }

  return { isValid: true };
};

/**
 * Gets file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot >= 0 ? filename.substring(lastDot) : '';
};

/**
 * Formats file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Formats date for display
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Unknown';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Gets a color for file extension badge
 */
export const getFileExtensionColor = (extension: string): string => {
  const ext = extension.toLowerCase();
  switch (ext) {
    case '.pdf':
      return '#dc3545'; // Red
    case '.doc':
    case '.docx':
      return '#007bff'; // Blue
    case '.xls':
    case '.xlsx':
      return '#28a745'; // Green
    case '.jpg':
    case '.jpeg':
    case '.png':
    case '.gif':
    case '.bmp':
    case '.tiff':
      return '#fd7e14'; // Orange
    case '.txt':
    case '.rtf':
      return '#6c757d'; // Gray
    case '.csv':
      return '#17a2b8'; // Cyan
    default:
      return '#6f42c1'; // Purple
  }
};

/**
 * Creates a download link for a blob
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Truncates filename for display
 */
export const truncateFilename = (filename: string, maxLength: number = 30): string => {
  if (filename.length <= maxLength) return filename;
  
  const extension = getFileExtension(filename);
  const nameWithoutExt = filename.substring(0, filename.length - extension.length);
  const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);
  
  return `${truncatedName}...${extension}`;
};
